import { useWorkspaceFileRecords } from "@/api/fileRecords";
import { useWorkspaces } from "@/api/workspaces";
import { CommandItem } from "@/components/ui/command";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  CascadeSub,
  CommandCascadeSub,
} from "@/object-actions/renderers/DestinationSubmenu";
import type { FileRecord, Workspace } from "@cohesive/api-client";
import { FileRecordTypeIcon } from "@cohesive/ui-components";
import { ChevronRight, Folder, Layers, LibraryBig, User } from "lucide-react";
import { useMemo, type ReactNode } from "react";

/**
 * "Add from your Library" as a nested pop — the symmetric twin of the Move
 * to / Copy to destination cascade, navigating the same Library tree in the
 * same stepped panels, but picking a FILE (the leaves) rather than a folder:
 * folders drill, clicking a file commits `onPick`. Reference semantics live
 * with the caller — the canvas mints a reference and places its node, the
 * composer attaches the record — so this component stays a pure picker.
 *
 * The top level lists workspaces — the current one first, then team
 * workspaces, private last (the old modal allowed browsing other
 * workspaces' Libraries too; a pick there mints a cross-workspace
 * reference, live in-app with A7's sandbox-shell gap). Each workspace
 * drills into its Library tree.
 *
 * Two hosts, mirroring the destination cascade: the composer's add-context
 * menu is command-shaped, the canvas insert/board menus are dropdowns.
 */

/** Current workspace first, then team alpha, private last. */
function useLibraryWorkspaces(currentWorkspaceId: string | undefined) {
  const { data: workspaces = [] } = useWorkspaces();
  return useMemo(
    () =>
      [...workspaces].sort((a, b) => {
        if (a.id === currentWorkspaceId) return -1;
        if (b.id === currentWorkspaceId) return 1;
        if (a.is_private !== b.is_private) return a.is_private ? 1 : -1;
        return a.name.localeCompare(b.name);
      }),
    [currentWorkspaceId, workspaces],
  );
}

function workspaceRowLabel(ws: Workspace): string {
  return ws.is_private ? "My Workspace" : ws.name;
}

interface LibraryNode {
  /** Folder path or the file's record — one of the two. */
  path: string;
  name: string;
  children: LibraryNode[];
  record?: FileRecord;
}

/** Folders first, then files, alpha within each — the rail's ordering. */
function buildLibraryTree(records: FileRecord[]): LibraryNode[] {
  const folders = new Map<string, LibraryNode>();
  const roots: LibraryNode[] = [];

  const nodeForFolder = (path: string): LibraryNode => {
    const existing = folders.get(path);
    if (existing) return existing;
    const trimmed = path.slice(0, -1);
    const name = trimmed.includes("/")
      ? trimmed.slice(trimmed.lastIndexOf("/") + 1)
      : trimmed;
    const node: LibraryNode = { path, name, children: [] };
    folders.set(path, node);
    const parentPath = trimmed.includes("/")
      ? trimmed.slice(0, trimmed.lastIndexOf("/") + 1)
      : "";
    if (parentPath) nodeForFolder(parentPath).children.push(node);
    else roots.push(node);
    return node;
  };

  for (const record of records) {
    if (record.path.endsWith("/")) nodeForFolder(record.path);
  }
  for (const record of records) {
    if (record.path.endsWith("/")) continue;
    const name = record.path.includes("/")
      ? record.path.slice(record.path.lastIndexOf("/") + 1)
      : record.path;
    const node: LibraryNode = {
      path: record.path,
      name,
      children: [],
      record,
    };
    const parentPath = record.path.includes("/")
      ? record.path.slice(0, record.path.lastIndexOf("/") + 1)
      : "";
    // A file whose folder marker is missing still surfaces at the root
    // rather than disappearing — same rule as the destination tree.
    if (parentPath && folders.has(parentPath)) {
      folders.get(parentPath)!.children.push(node);
    } else if (parentPath) {
      nodeForFolder(parentPath).children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortLevel = (nodes: LibraryNode[]) => {
    nodes.sort((a, b) => {
      const aFolder = !a.record;
      const bFolder = !b.record;
      if (aFolder !== bFolder) return aFolder ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    for (const node of nodes) sortLevel(node.children);
  };
  sortLevel(roots);
  return roots;
}

function LibraryNodeRows({
  nodes,
  onPick,
}: {
  nodes: LibraryNode[];
  onPick: (record: FileRecord) => void;
}) {
  return (
    <>
      {nodes.map((node) =>
        node.record ? (
          <CommandItem
            key={node.path}
            value={`library-pick-${node.path}`}
            onSelect={() => onPick(node.record!)}
          >
            <FileRecordTypeIcon
              contentType={node.record.content_type}
              originType={node.record.origin_type}
              className="size-4 shrink-0"
            />
            <span className="min-w-0 truncate">{node.name}</span>
          </CommandItem>
        ) : (
          <CommandCascadeSub
            key={node.path}
            value={`library-folder-${node.path}`}
            disabled={node.children.length === 0}
            row={
              <>
                <Folder className="text-foreground/60" />
                <span className="min-w-0 truncate">{node.name}</span>
                <ChevronRight className="ml-auto h-4 w-4 opacity-60" />
              </>
            }
          >
            <LibraryNodeRows nodes={node.children} onPick={onPick} />
          </CommandCascadeSub>
        ),
      )}
    </>
  );
}

function LibraryNodeDropdownRows({
  nodes,
  onPick,
}: {
  nodes: LibraryNode[];
  onPick: (record: FileRecord) => void;
}) {
  return (
    <>
      {nodes.map((node) =>
        node.record ? (
          <DropdownMenuItem
            key={node.path}
            onClick={(e) => {
              e.stopPropagation();
              onPick(node.record!);
            }}
          >
            <FileRecordTypeIcon
              contentType={node.record.content_type}
              originType={node.record.origin_type}
              className="size-4 shrink-0"
            />
            <span className="min-w-0 truncate">{node.name}</span>
          </DropdownMenuItem>
        ) : (
          <CascadeSub
            key={node.path}
            disabled={node.children.length === 0}
            row={
              <>
                <Folder className="text-foreground/60" />
                <span className="min-w-0 truncate">{node.name}</span>
              </>
            }
          >
            <LibraryNodeDropdownRows nodes={node.children} onPick={onPick} />
          </CascadeSub>
        ),
      )}
    </>
  );
}

function DropdownWorkspaceLibrary({
  workspace,
  onPick,
}: {
  workspace: Workspace;
  onPick: (record: FileRecord) => void;
}) {
  const { data: records = [], isLoading } = useWorkspaceFileRecords(
    workspace.id,
  );
  const tree = useMemo(() => buildLibraryTree(records), [records]);
  const Icon = workspace.is_private ? User : Layers;

  return (
    <CascadeSub
      disabled={!isLoading && tree.length === 0}
      row={
        <>
          <Icon className="text-foreground/60" />
          <span className="min-w-0 truncate">
            {workspaceRowLabel(workspace)}
          </span>
        </>
      }
    >
      {tree.length === 0 ? (
        <div className="px-2 py-4 text-center text-sm text-muted-foreground">
          {isLoading ? "Loading…" : "The Library is empty."}
        </div>
      ) : (
        <LibraryNodeDropdownRows nodes={tree} onPick={onPick} />
      )}
    </CascadeSub>
  );
}

/**
 * Dropdown-host variant for the canvas insert and board context menus.
 */
export function LibraryFileDropdownCascade({
  workspaceId,
  onPick,
  label = "Add from Library",
}: {
  /** The surface's own workspace — listed first. */
  workspaceId: string | undefined;
  onPick: (record: FileRecord) => void;
  label?: string;
}) {
  const workspaces = useLibraryWorkspaces(workspaceId);

  if (!workspaceId) return null;

  return (
    <CascadeSub
      disabled={workspaces.length === 0}
      row={
        <>
          <LibraryBig className="size-4 shrink-0 text-foreground/60" />
          <span className="min-w-0 truncate">{label}</span>
        </>
      }
    >
      {workspaces.map((ws) => (
        <DropdownWorkspaceLibrary key={ws.id} workspace={ws} onPick={onPick} />
      ))}
    </CascadeSub>
  );
}

function CommandWorkspaceLibrary({
  workspace,
  onPick,
}: {
  workspace: Workspace;
  onPick: (record: FileRecord) => void;
}) {
  const { data: records = [], isLoading } = useWorkspaceFileRecords(
    workspace.id,
  );
  const tree = useMemo(() => buildLibraryTree(records), [records]);
  const Icon = workspace.is_private ? User : Layers;

  return (
    <CommandCascadeSub
      value={`library-ws-${workspace.id}`}
      disabled={!isLoading && tree.length === 0}
      row={
        <>
          <Icon className="text-foreground/60" />
          <span className="min-w-0 truncate">
            {workspaceRowLabel(workspace)}
          </span>
          <ChevronRight className="ml-auto h-4 w-4 opacity-60" />
        </>
      }
    >
      {tree.length === 0 ? (
        <div className="px-2 py-4 text-center text-sm text-muted-foreground">
          {isLoading ? "Loading…" : "The Library is empty."}
        </div>
      ) : (
        <LibraryNodeRows nodes={tree} onPick={onPick} />
      )}
    </CommandCascadeSub>
  );
}

/**
 * The "Add from your Library" row plus its cascade: workspaces, then each
 * one's Library tree. Renders nothing when no current workspace exists to
 * lead the list (pre-session composers with no home Library).
 */
export function LibraryFileCommandCascade({
  workspaceId,
  onPick,
  label = "Add from Library",
  row,
}: {
  /** The surface's own workspace — listed first. */
  workspaceId: string | undefined;
  onPick: (record: FileRecord) => void;
  label?: string;
  /** Custom trigger row content; defaults to the Library glyph + label. */
  row?: ReactNode;
}) {
  const workspaces = useLibraryWorkspaces(workspaceId);

  if (!workspaceId) return null;

  return (
    <CommandCascadeSub
      value="add-from-library"
      disabled={workspaces.length === 0}
      row={
        row ?? (
          <>
            <LibraryBig className="size-4 shrink-0 text-foreground/60" />
            <span className="min-w-0 truncate">{label}</span>
            <ChevronRight className="ml-auto h-4 w-4 opacity-60" />
          </>
        )
      }
    >
      {workspaces.map((ws) => (
        <CommandWorkspaceLibrary key={ws.id} workspace={ws} onPick={onPick} />
      ))}
    </CommandCascadeSub>
  );
}
